/**
 * Typed result wrapper for external API calls.
 *
 * Replaces silent fallbacks (`return 0`, `return {}`, `return 'US'`) with an
 * explicit discriminated union so every caller must decide what to do when a
 * source is missing. Downstream, the route-store aggregates these into
 * `CalculationWarnings` which the UI surfaces as honest banners.
 *
 * Contract:
 *   - `ok: true, source: 'fresh' | 'cached'` — real data, trustworthy
 *   - `ok: true, source: 'fallback'` — degraded data (e.g. national avg prices
 *      when EIA is down). Safe to show with a warning banner.
 *   - `ok: false, source: 'unavailable'` — no usable data at all. Caller must
 *      either proceed without this source or hard-fail.
 *
 * The `reason` field is human-readable and surfaced to the user.
 */

export type ApiSource = 'fresh' | 'cached' | 'fallback' | 'unavailable'

export type ApiResult<T> =
	| { ok: true; data: T; source: 'fresh' | 'cached'; fetchedAt: number }
	| { ok: true; data: T; source: 'fallback'; fetchedAt: number; reason: string }
	| { ok: false; source: 'unavailable'; reason: string }

/** Successful fresh fetch — the happy path. */
export function ok<T>(data: T, source: 'fresh' | 'cached' = 'fresh'): ApiResult<T> {
	return { ok: true, data, source, fetchedAt: Date.now() }
}

/** Degraded success — we have SOMETHING but it's not the best available data. */
export function fallback<T>(data: T, reason: string): ApiResult<T> {
	return { ok: true, data, source: 'fallback', fetchedAt: Date.now(), reason }
}

/** Total failure — no usable data. Caller decides whether to hard-fail or skip. */
export function unavailable<T>(reason: string): ApiResult<T> {
	return { ok: false, source: 'unavailable', reason }
}

/**
 * Fetch with timeout + one retry on transient failures (network errors, 5xx).
 * Returns the Response on success or throws on persistent failure.
 *
 * Default 12s accommodates: (a) EIA cold-cache fills (~3s) + network jitter,
 * (b) Mapbox tilequery batches under load. Individual callers can pass a
 * larger value for endpoints that are known-slow on cold start.
 *
 * @param input - fetch input
 * @param init - fetch init (signal is overridden with timeout)
 * @param timeoutMs - hard timeout per attempt (default 12s)
 */
export async function fetchWithTimeout(
	input: RequestInfo | URL,
	init: RequestInit = {},
	timeoutMs = 12000,
): Promise<Response> {
	let lastErr: unknown
	for (let attempt = 0; attempt < 2; attempt++) {
		try {
			const signal = init.signal ?? AbortSignal.timeout(timeoutMs)
			const res = await fetch(input, { ...init, signal })
			// Retry on 5xx (transient server errors), but not on 4xx (client errors)
			if (res.status >= 500 && attempt === 0) {
				lastErr = new Error(`upstream ${res.status}`)
				continue
			}
			return res
		} catch (err) {
			lastErr = err
			// If the caller's own signal aborted, don't retry
			if (init.signal?.aborted) throw err
		}
	}
	throw lastErr instanceof Error ? lastErr : new Error('fetch failed')
}
